import { CID } from 'multiformats/cid';

import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import type { FileMetadata, ProgressCallback } from '@/types/poc/helia';
import { addFileMetadata } from '@/store/slices/poc/heliaSlice';

const DEFAULT_MAX_WAIT_TIME = 300000; // 5 minutes
const PROGRESS_COMPLETE = 100;
const PROGRESS_START = 0;

export interface DownloadNetworkFileParams {
  sixId: string;
  cid: string;
  metadata?: FileMetadata;
  onProgress?: ProgressCallback;
  onChunkReceived?: (bytesLoaded: number) => void;
  signal?: AbortSignal;
  maxWaitTime?: number;
}

/**
 * Download a file from the network via Bitswap.
 *
 * fs.cat() triggers Bitswap to fetch missing blocks on-demand as it iterates
 * through the DAG. No polling loop is needed — Bitswap resolves each block as
 * the iterator requests it. An AbortSignal provides timeout protection.
 */
export const downloadFileFromNetwork = createAppAsyncThunk(
  'helia/downloadFromNetwork',
  async(
    {
      sixId,
      cid,
      metadata,
      onProgress,
      onChunkReceived,
      signal: externalSignal,
      maxWaitTime = DEFAULT_MAX_WAIT_TIME,
    }: DownloadNetworkFileParams,
    { extra: { pocServices, loggerService }, dispatch }
  ) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:p2p:files:network' });
    log.info('Downloading file from network, CID:', cid);
    const { p2pStackManagerService, networkFileService } = pocServices;
    const helia = p2pStackManagerService.getHeliaNode(sixId);

    if (helia == null) {
      throw new Error('Helia node not initialized');
    }

    const parsedCid = CID.parse(cid);

    // Check if file is already fully local (pinned = all DAG blocks present)
    const isPinned = await helia.helia.pins.isPinned(parsedCid);

    if (isPinned) {
      log.debug('File already pinned locally, reading from blockstore');
      const fileResult = await networkFileService.getFileData(helia, cid);
      const fileMetadata = networkFileService.createFileMetadata(cid, fileResult.size, sixId, metadata);

      dispatch(addFileMetadata(fileMetadata));

      if (onProgress !== undefined) onProgress(PROGRESS_COMPLETE, PROGRESS_COMPLETE);

      return {
        cid,
        metadata: fileMetadata,
        bytesDownloaded: fileResult.size,
        source: 'local' as const
      };
    }

    // File not local — fetch via Bitswap with timeout protection.
    // fs.cat() (called by getFileData) traverses the DAG and triggers Bitswap
    // for each missing block on-demand. No polling needed.
    if (onProgress !== undefined) onProgress(PROGRESS_START, PROGRESS_COMPLETE);
    log.debug('File not pinned locally, fetching via Bitswap...');
    pocServices.cidDownloadLogger.log(cid, 'requesting', { expectedSize: metadata?.size });

    const timeoutSignal = AbortSignal.timeout(maxWaitTime);
    const signal = externalSignal != null
      ? AbortSignal.any([externalSignal, timeoutSignal])
      : timeoutSignal;

    try {
      const fileResult = await networkFileService.getFileData(helia, cid, { signal, onChunkReceived });
      const fileMetadata = networkFileService.createFileMetadata(cid, fileResult.size, sixId, metadata);

      pocServices.cidDownloadLogger.log(cid, 'stored', { totalBytes: fileResult.size });
      dispatch(addFileMetadata(fileMetadata));

      if (onProgress !== undefined) onProgress(PROGRESS_COMPLETE, PROGRESS_COMPLETE);

      return {
        cid,
        metadata: fileMetadata,
        bytesDownloaded: fileResult.size,
        source: 'network' as const
      };
    } catch (error) {
      if (signal.aborted) {
        pocServices.cidDownloadLogger.log(cid, 'error', { error: `Timeout after ${maxWaitTime}ms` });
        throw new Error(`Timeout: File ${cid} did not download within ${maxWaitTime}ms`);
      }
      throw error;
    }
  }
);

/**
 * Save a file from Helia to the user's filesystem
 */
export const saveFileToFilesystem = createAppAsyncThunk(
  'helia/saveToFilesystem',
  async(
    {
      sixId,
      cid,
      filename,
      mimeType
    }: {
      sixId: string;
      cid: string;
      filename: string;
      mimeType?: string;
    },
    { extra: { pocServices } }
  ) => {
    const { p2pStackManagerService, networkFileService } = pocServices;

    const helia = p2pStackManagerService.getHeliaNode(sixId);
    if (helia === null) {
      throw new Error('Helia node not initialized');
    }

    const fileResult = await networkFileService.getFileData(helia, cid);
    networkFileService.saveToFilesystem(fileResult.data, filename, mimeType);

    return { cid, filename, size: fileResult.size };
  }
);
